import type { Messages } from '../catalogue.en'

/**
 * Correspondence, the patient's message register and communication
 * preferences, in Russian.
 *
 * The delivery words keep the English module's distinctions exactly: «принято
 * провайдером» is the provider taking the message and «доставлено» is the
 * recipient having it, and no Russian word is allowed to stand for both.
 *
 * Composed into the public catalogue by `catalogue.ru.ts`. Nothing imports
 * this module directly: consumers ask the catalogue, so how the messages are
 * stored stays this layer's business.
 */
export const correspondence: Partial<Messages> = {
  'correspondence.refusal.sourceInactive': 'До отправки событие было закрыто или его получатель изменился.',
  'correspondence.refusal.protectedPayloadUnavailable': 'Не удалось подготовить защищённую ссылку на документ. Обратитесь к администратору клиники.',
  'correspondence.listLabel': 'Переписка с этим пациентом',
  'correspondence.empty': 'Переписки пока нет',
  'correspondence.emptyDetail':
    'Здесь будут показаны сообщения, которыми обменялись с пациентом по электронной почте, в Telegram или WhatsApp.',
  'correspondence.truncated':
    'В этом указателе клиника хранит только новейшие переписки (не более {count}), поэтому более старые здесь могут отсутствовать.',
  'correspondence.refresh': 'Обновить',

  'correspondence.direction.inbound': 'Получено',
  'correspondence.direction.outbound': 'Отправлено',
  'correspondence.actorKind.provider': 'через провайдера',
  'correspondence.actorKind.service': 'автоматически',

  'correspondence.channel.email': 'Электронная почта',
  'correspondence.channel.telegram': 'Telegram',
  'correspondence.channel.whatsapp': 'WhatsApp',
  'correspondence.channel.inApp': 'В приложении',

  'correspondence.to': 'Кому: {name}, {address}',
  'correspondence.from': 'От: {name}, {address}',
  'correspondence.endpointStatus': 'Адрес на момент записи: {status}',
  'correspondence.purpose': 'Назначение: {purpose}',
  'correspondence.noSubject': 'Без темы',
  'correspondence.mediaOnly': 'Содержало вложение без текста. Вложения здесь не показываются.',
  'correspondence.webAppButton': 'Предложена кнопка «{text}»',

  'correspondence.state.accepted': 'Отправлено',
  'correspondence.state.delivered': 'Доставлено',
  'correspondence.state.undelivered': 'Не доставлено',
  'correspondence.state.read': 'Прочитано',
  'correspondence.state.received': 'Получено',
  'correspondence.retrying': 'Повторная попытка после сбоя',

  'correspondence.refusal.connectorMismatch': 'Подключение для сообщений настроено неверно. Обратитесь к администратору клиники.',
  'correspondence.refusal.endpointAmbiguous': 'Для получателя настроено несколько адресов. Попросите администратора проверить контакты.',
  'correspondence.refusal.serviceUnavailable': 'Сервис отправки недоступен. Доставка не подтверждена.',
  'correspondence.refusal.telegramRejected': 'Телеграм отклонил сообщение. Проверьте, что пациент запустил бота и не заблокировал его.',
  'correspondence.refusal.messageInvalid': 'Канал не может отправить это сообщение. Проверьте его длину и содержимое.',
  'correspondence.refusal.unknown': 'Канал не смог отправить сообщение. Обратитесь к администратору клиники.',
  'correspondence.refusal.noSelectedChannels': 'Для получателя не выбран канал связи.',
  'correspondence.refusal.routeMissing': 'для этого канала не настроен маршрут',
  'correspondence.refusal.routeAmbiguous': 'для этого канала настроено больше одного маршрута',
  'correspondence.refusal.routeChanged': 'маршрут изменился во время записи сообщения',
  'correspondence.refusal.endpointUnverified': 'адрес не был подтверждён',
  'correspondence.refusal.preferenceRequired': 'пациент не выбирал этот канал',
  'correspondence.refusal.preferenceDenied': 'пациент отказался от этого канала',
  'correspondence.refusal.connectorUnavailable': 'подключение было недоступно',
  'correspondence.refused': 'Отклонено: {reason}',
  'correspondence.refusedCode': 'Отклонено с кодом {code}',

  'correspondence.historyShow': 'История доставки',
  'correspondence.historyHide': 'Скрыть историю доставки',
  'correspondence.historyLabel': 'История доставки сообщения от {time}',
  'correspondence.historyEmpty': 'Провайдер пока ничего не сообщил об этом сообщении.',
  'correspondence.historyRefused': 'У вас нет доступа к истории доставки.',
  'correspondence.historyFailed': 'Не удалось загрузить историю доставки.',
  'correspondence.attempt': 'Попытка {n}',
  'correspondence.attemptUnknown': 'Отчёт провайдера',
  'correspondence.eventSimulated': 'имитация',
  'correspondence.eventDetail': '{state} — {detail}',

  'correspondence.provenance.whatsAppProvisional': 'Личность в WhatsApp ещё не подтверждена',
  'correspondence.provenance.whatsAppVerified': 'Личность в WhatsApp подтверждена',
  'correspondence.provenance.lead': 'Началось как обращение до создания этой карты',

  'correspondence.compose': 'Написать сообщение',
  'correspondence.reply': 'Ответить',
  'correspondence.replyTitle': 'Ответ через {channel}',
  'correspondence.replyingTo': 'Ответ в переписке {channel} на сообщение от {time}.',
  'correspondence.replyCancel': 'Написать новое сообщение',
  'correspondence.channelLabel': 'Канал',
  'correspondence.channelHint':
    'Предлагаются только каналы с подтверждённым адресом, выбором пациента и включённым маршрутом.',
  'correspondence.channelWithheld': '{channel}: {reason}',
  'correspondence.withheld.noPermission': 'вы не можете отправлять по этому каналу',
  'correspondence.withheld.noEndpoint': 'нет подтверждённого адреса',
  'correspondence.withheld.notAllowed': 'пациент не выбирал этот канал',
  'correspondence.withheld.denied': 'пациент отказался',
  'correspondence.withheld.noRoute': 'нет включённого маршрута',
  'correspondence.withheld.unknown': 'не удалось проверить адрес и выбор пациента',
  'correspondence.noChannel': 'Сейчас для этого пациента нет доступного канала.',
  'correspondence.needChannel': 'Перед отправкой выберите канал.',
  'correspondence.chatSubjectNote': 'В Telegram и WhatsApp тема уходит первой строкой.',
  'correspondence.send': 'Отправить',
  'correspondence.sending': 'Отправка…',
  'correspondence.recorded': 'Записано и передано в {channel}.',
  'correspondence.recordedRefused': 'Записано, но доставка отклонена: {reason}.',
  'correspondence.sendMismatch':
    'Эта переписка больше не соответствует выбранному каналу. Текст сохранён — выберите канал заново.',
  'correspondence.sendStale':
    'Канал больше недоступен. Текст сохранён — выберите другой канал.',
  'correspondence.sendEndpointGone':
    'Этот адрес больше недоступен. Текст сохранён — выберите другой канал.',
  'message.sectionTitle': 'Сообщения',
  'message.tableLabel': 'Сообщения, отправленные этому пациенту',
  'message.readRefused': 'У вас нет доступа к сообщениям этого пациента.',
  'message.readFailed': 'Не удалось загрузить сообщения.',
  'message.empty': 'Пока ничего не отправлено',
  'message.emptyDetail':
    'Здесь будут перечислены сообщения, написанные этому пациенту.',
  'message.column.subject': 'Тема',
  'message.column.author': 'Автор',
  'message.column.sent': 'Отправлено',
  'message.column.state': 'Доставка',
  'message.column.addressedTo': 'Адресовано {address}',
  'message.composerTitle': 'Написать сообщение',
  'message.recipientLabel': 'Получатель',
  'message.recipientPlaceholder': 'Получатель не выбран',
  'message.patientLabel': 'Пациент',
  'message.recipientAllGroup': 'Все получатели',
  'message.recipientNoMatches': 'Нет подходящих получателей',
  'message.recipientLoadFailed': 'Не удалось загрузить получателей.',
  'message.noRecipientChosen': 'Выберите, кому адресовано это сообщение.',
  'message.noPatientChosenFiles': 'Выберите, для кого предназначены эти файлы.',
  'message.badgeNoAddress': 'адрес электронной почты не указан',
  'message.subjectLabel': 'Тема',
  'message.bodyLabel': 'Сообщение',
  'message.signatureTitle': 'Ваша подпись',
  'message.signatureEmpty':
    'Вы не подготовили подпись. Сообщение будет отправлено без неё.',
  'message.signatureEdit': 'Измените её в своём профиле',
  'message.send': 'Отправить',
  'message.sending': 'Отправка…',
  'message.needRecipient': 'Выберите получателя перед отправкой.',
  'message.needSubject': 'Напишите тему перед отправкой.',
  'message.needBody': 'Напишите сообщение перед отправкой.',
  'message.needPatientForFiles':
    'Перед добавлением этих файлов выберите пациента.',
  'message.sendNoAddress':
    'У этого получателя не указан адрес электронной почты, поэтому отправлять некуда.',
  'message.sendInvalid': 'Сообщение отклонено: проверьте тему и текст.',
  'message.sendTooLong': 'Сообщение слишком длинное для отправки.',
  'message.sendConflict':
    'Это сообщение уже отправлено. Измените его, чтобы отправить другое.',
  'message.sendForbidden': 'У вас нет разрешения на отправку сообщений.',
  'message.sendRecipientGone': 'Получатель больше недоступен.',
  'message.sendSessionEnded':
    'Ваш сеанс завершён. Войдите снова, чтобы отправить.',
  'message.sendUnavailable':
    'Не удалось отправить сообщение. Попробуйте ещё раз.',
  'message.sendFailed': 'Не удалось отправить сообщение.',
  'message.state.partial': 'Результаты доставки различаются по каналам',
  'message.noSelectedChannels':
    'Сообщение не отправлено: у пациента нет доступного канала связи.',
  'message.internalFallback':
    'Внешняя доставка не удалась. Сообщение доступно получателю во внутренних уведомлениях.',
  'message.channelOutcome': '{channel}: {state}',
  'message.state.pending': 'Отправляется…',
  'message.state.sent': 'Отправлено',
  'message.state.simulated': 'Не доставлено — демонстрация',
  'message.state.failed': 'Не отправлено',
  'message.outcomeUnavailable':
    'Не удалось обновить статус доставки. Проверьте соединение; пока не отправляйте копию сообщения.',
  'message.outcomeAddressed': 'Адресовано {name} на {address}.',
  'message.discardTitle': 'Отменить это сообщение?',
  'message.discardDetail': 'Написанное вами не будет сохранено.',
  'message.keepEditing': 'Продолжить писать',
  'message.discardConfirm': 'Отменить',
  'message.unsavedIntent': 'У вас есть неотправленное сообщение.',

  'commPrefs.title': 'Настройки связи',
  'commPrefs.readRefused': 'У вас нет доступа к настройкам связи этого человека.',
  'commPrefs.readFailed': 'Не удалось загрузить настройки связи.',
  'commPrefs.edit': 'Изменить настройки связи',
  'commPrefs.readOnly': 'Вы можете просматривать эти настройки, но не изменять их.',
  'commPrefs.endpointsTitle': 'Подтверждённые адреса',
  'commPrefs.endpoint.status.provisional': 'ожидает подтверждения',
  'commPrefs.endpoint.status.verified': 'подтверждён',
  'commPrefs.endpoint.status.revoked': 'отозван',

  'commPrefs.purpose.patientCommunications': 'Чат с клиникой',
  'commPrefs.purpose.patientMessage': 'Сообщения от клиники',
  'commPrefs.purpose.treatmentCostEstimate': 'Расчёты стоимости лечения',
  'commPrefs.purpose.onboardingVerification': 'Подтверждение при регистрации',
  'commPrefs.purpose.staffCommunications': 'Служебные сообщения клиники',
  'commPrefs.purpose.taskReminder': 'Напоминания о задачах',

  'commPrefs.messageTypes': 'Сообщения',
  'commPrefs.blockMessages': 'Запретить передачу сообщений',
  'commPrefs.messagesBlocked': 'Передача сообщений запрещена.',
  'commPrefs.unselectedNote':
    'Каждый выбранный тип сообщений уходит по каждому выбранному каналу; всё неотмеченное — отказ.',
  'commPrefs.channels': 'Каналы',
  'commPrefs.channelLinkNote': 'Для доставки также нужен подтверждённый адрес или привязанный мессенджер.',
  'commPrefs.hours.title': 'Часы уведомлений',
  'commPrefs.hours.detail':
    'Обычные уведомления, ежедневно, {zone}. Интервал может переходить через полночь. Срочные события отправляются сразу в любом случае.',
  'commPrefs.hours.always': 'В любое время',
  'commPrefs.hours.from': 'С',
  'commPrefs.hours.until': 'До',
  'commPrefs.hours.window': '{from} – {until}',
  'commPrefs.hours.invalid': 'Укажите разное время начала и окончания.',
  'commPrefs.incompleteSelection':
    'Выберите хотя бы один тип сообщений и один канал или снимите все отметки в обеих группах, чтобы отказаться от всех необязательных сообщений.',

  'commPrefs.saveSettings': 'Сохранить настройки',
  'commPrefs.saving': 'Сохранение…',
  'commPrefs.cancel': 'Отмена',
  'commPrefs.saved': 'Сохранено.',
  'commPrefs.batchFailed': 'Не удалось сохранить настройки. Перед повторной попыткой проверьте сохранённые настройки.',
  'commPrefs.batchConflict':
    'Настройки изменены в другом месте, и это сохранение отклонено. Загрузите сохранённые настройки перед новым выбором.',
  'commPrefs.saveForbidden': 'У вас нет права изменять настройки связи.',
  'commPrefs.saveInvalid': 'Настройки отклонены: проверьте поля.',
  'commPrefs.saveSessionEnded': 'Сеанс завершён. Войдите снова, чтобы сохранить.',
  'commPrefs.reloadSettings': 'Загрузить сохранённые настройки',
  'commPrefs.reloadFailed': 'Не удалось загрузить настройки. Ваш черновик сохранён.',
  'commPrefs.reloaded': 'Черновик заменён сохранёнными настройками.',
  'commPrefs.unsavedIntent': 'Настройки связи с пациентом',
  'commPrefs.staffUnsavedIntent': 'Ваши настройки связи',
}
